import React, { useState, useEffect, useRef } from 'react';
import { 
  CreditCard, Calendar, Clock, AlertTriangle, CheckCircle, 
  FileText, DollarSign, AlertCircle, Ban, Download, Eye, Printer, X,
  Building2, School, Banknote, Copy, Check, RefreshCw, Loader2
} from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const GroupSubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState({ pending_invoices: [], paid_invoices: [], total_pending: 0 });
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [copied, setCopied] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'bank_transfer',
    transaction_id: '',
    notes: ''
  });
  const invoicePrintRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [subRes, invRes, bankRes] = await Promise.all([
        axios.get(`${API_URL}/api/groups/my-group/subscription`, { headers }),
        axios.get(`${API_URL}/api/groups/my-group/invoices`, { headers }),
        axios.get(`${API_URL}/api/groups/payment/bank-details`, { headers }).catch(() => ({ data: { bank_details: null } }))
      ]);

      setSubscription(subRes.data);
      setInvoices(invRes.data);
      setBankDetails(bankRes.data?.bank_details);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (e) => {
    e.preventDefault();
    if (!paymentForm.transaction_id.trim()) {
      alert('Please enter Transaction ID');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/groups/my-group/pay-invoice`,
        {
          invoice_id: selectedInvoice.id,
          payment_method: paymentForm.payment_method,
          transaction_id: paymentForm.transaction_id,
          notes: paymentForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Payment successful! Subscription extended until ${res.data.new_end_date}`);
      setShowPayModal(false);
      setPaymentForm({ payment_method: 'bank_transfer', transaction_id: '', notes: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const handlePrintInvoice = () => {
    const printContent = invoicePrintRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${selectedInvoice?.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .invoice-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .invoice-header h1 { color: #7c3aed; margin: 0; font-size: 28px; }
            .invoice-header p { color: #666; margin: 5px 0; }
            .invoice-details { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .invoice-details div { flex: 1; }
            .invoice-details h3 { color: #333; margin-bottom: 10px; font-size: 14px; text-transform: uppercase; }
            .invoice-details p { margin: 5px 0; color: #555; }
            .invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            .invoice-table th, .invoice-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .invoice-table th { background: #f5f5f5; font-weight: bold; }
            .total-row { font-weight: bold; background: #f0f9ff; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
            .status-paid { background: #dcfce7; color: #166534; }
            .status-pending { background: #fef3c7; color: #92400e; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Bug fix: removed shadowing formatDate – using imported one

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const sub = subscription?.subscription || {};
  const group = subscription?.group || {};
  const daysRemaining = subscription?.days_remaining || 0;
  const isExpiringSoon = subscription?.is_expiring_soon;
  const isExpired = subscription?.is_expired;
  const collegesUsed = subscription?.colleges_used || 0;

  // Combine all invoices for history
  const allInvoices = [...(invoices.pending_invoices || []), ...(invoices.paid_invoices || [])];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="group-subscription-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Software Subscription</h1>
          <p className="text-gray-600 mt-1">{group.name}</p>
        </div>
        <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg" title="Refresh">
          <RefreshCw className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Warning Banner if Blocked */}
      {sub.is_blocked && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Ban className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800">Your Group is Blocked</h3>
            <p className="text-red-700 text-sm mt-1">
              Access restricted due to non-payment. Clear pending invoices to restore access.
            </p>
          </div>
        </div>
      )}

      {/* Warning Banner if Expiring Soon */}
      {isExpiringSoon && !sub.is_blocked && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Clock className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-yellow-800">Subscription Expiring Soon</h3>
            <p className="text-yellow-700 text-sm mt-1">
              Your subscription will expire in <strong>{daysRemaining} days</strong>. 
              Pay the pending invoice to avoid service interruption.
            </p>
          </div>
        </div>
      )}

      {/* Subscription Status Card */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6 mb-6 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-purple-200 text-sm">Current Plan</p>
            <p className="text-2xl font-bold capitalize mt-1">{sub.plan_type || 'N/A'}</p>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
              sub.status === 'active' ? 'bg-green-400 text-green-900' : 
              sub.status === 'grace_period' ? 'bg-yellow-400 text-yellow-900' :
              'bg-red-400 text-red-900'
            }`}>
              {sub.status?.toUpperCase() || 'Unknown'}
            </span>
          </div>
          
          <div>
            <p className="text-purple-200 text-sm">Valid Until</p>
            <p className="text-2xl font-bold mt-1">{formatDate(sub.end_date)}</p>
            <p className={`text-sm mt-2 ${daysRemaining < 30 ? 'text-yellow-300' : 'text-purple-200'}`}>
              {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Expired'}
            </p>
          </div>
          
          <div>
            <p className="text-purple-200 text-sm">Plan Amount</p>
            <p className="text-2xl font-bold mt-1">₹{(sub.amount || 0).toLocaleString('en-IN')}</p>
            <p className="text-purple-200 text-sm mt-2">/{sub.plan_type || 'month'}</p>
          </div>
          
          <div>
            <p className="text-purple-200 text-sm">College Slots</p>
            <p className="text-2xl font-bold mt-1">{collegesUsed} / {sub.max_colleges === -1 ? '∞' : sub.max_colleges}</p>
            <p className="text-purple-200 text-sm mt-2">
              {(sub.max_colleges === -1 ? 'Unlimited' : sub.max_colleges - collegesUsed)} available
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Max Students</p>
              <p className="text-xl font-bold">{sub.max_students === -1 ? 'Unlimited' : sub.max_students?.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Max Staff</p>
              <p className="text-xl font-bold">{sub.max_staff === -1 ? 'Unlimited' : sub.max_staff?.toLocaleString('en-IN')}</p>
            </div>
            <School className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Amount</p>
              <p className="text-xl font-bold text-orange-600">₹{(invoices.total_pending || 0).toLocaleString('en-IN')}</p>
            </div>
            <FileText className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Invoices Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoices ({allInvoices.length})
          </h2>
        </div>

        {allInvoices.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No invoices yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {allInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium">{invoice.invoice_number}</p>
                      <p className="text-xs text-gray-400 capitalize">{invoice.plan_type}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                    </td>
                    <td className="px-4 py-3 font-bold">₹{invoice.amount?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(invoice.due_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setSelectedInvoice(invoice); setShowInvoiceModal(true); }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-500"
                          title="View Invoice"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {invoice.status !== 'paid' && (
                          <button
                            onClick={() => { setSelectedInvoice(invoice); setShowPayModal(true); }}
                            className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                          >
                            Pay Now
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bank Details - Only show if configured (not default placeholder) */}
      {bankDetails && bankDetails.account_number && bankDetails.account_number !== '1234567890123456' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Banknote className="h-5 w-5" />
            Payment Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Bank Name', value: bankDetails.bank_name },
              { label: 'Account Holder', value: bankDetails.account_holder },
              { label: 'Account Number', value: bankDetails.account_number, copyable: true },
              { label: 'IFSC Code', value: bankDetails.ifsc_code, copyable: true },
              { label: 'Branch', value: bankDetails.branch_name },
              { label: 'UPI ID', value: bankDetails.upi_id, copyable: true }
            ].filter(item => item.value).map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">{item.label}</p>
                  <p className="font-medium">{item.value}</p>
                </div>
                {item.copyable && (
                  <button onClick={() => copyToClipboard(item.value, item.label)} className="p-1 hover:bg-gray-200 rounded">
                    {copied === item.label ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-4">
            * After making payment, click "Pay Now" and enter your Transaction ID.
          </p>
        </div>
      )}

      {/* Invoice View Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold">Invoice Details</h2>
              <div className="flex items-center gap-2">
                <button onClick={handlePrintInvoice} className="p-2 hover:bg-gray-100 rounded" title="Print">
                  <Printer className="h-5 w-5" />
                </button>
                <button onClick={() => setShowInvoiceModal(false)} className="p-2 hover:bg-gray-100 rounded">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div ref={invoicePrintRef} className="p-6">
              <div className="invoice-header text-center mb-6 pb-4 border-b-2">
                <h1 className="text-2xl font-bold text-purple-600">INVOICE</h1>
                <p className="text-gray-500">{selectedInvoice.invoice_number}</p>
              </div>

              {/* Company & Invoice Details */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="font-bold text-sm text-gray-500 uppercase mb-2">From</h3>
                  <p className="font-bold">Ncriptech Global Pvt Limited</p>
                  <p className="text-gray-600 text-sm">2nd Floor, Rainbow Apartment</p>
                  <p className="text-gray-600 text-sm">Kanaipur, Konnagar</p>
                  <p className="text-gray-600 text-sm">West Bengal - 712246</p>
                  <p className="text-gray-600 text-sm">Phone: 9836807060</p>
                </div>
                <div className="text-right">
                  <h3 className="font-bold text-sm text-gray-500 uppercase mb-2">Invoice Details</h3>
                  <p>Date: {formatDate(selectedInvoice.created_at)}</p>
                  <p>Due: {formatDate(selectedInvoice.due_date)}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                    selectedInvoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedInvoice.status?.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Bill To */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-bold text-sm text-gray-500 uppercase mb-2">Billed To</h3>
                <p className="font-bold">{group.name}</p>
                <p className="text-gray-600">{group.code}</p>
              </div>

              <table className="w-full mb-6 border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left border">Description</th>
                    <th className="p-3 text-right border">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border">
                      <p className="font-medium">Software Subscription - {selectedInvoice.plan_type?.toUpperCase()}</p>
                      <p className="text-sm text-gray-500">
                        Period: {formatDate(selectedInvoice.billing_period_start)} to {formatDate(selectedInvoice.billing_period_end)}
                      </p>
                    </td>
                    <td className="p-3 border text-right">
                      ₹{selectedInvoice.base_amount ? selectedInvoice.base_amount?.toLocaleString('en-IN') : selectedInvoice.amount?.toLocaleString('en-IN')}
                    </td>
                  </tr>
                  {selectedInvoice.gst_amount > 0 && (
                    <tr>
                      <td className="p-3 border text-gray-600">GST ({selectedInvoice.gst_percent || 18}%)</td>
                      <td className="p-3 border text-right text-gray-600">₹{selectedInvoice.gst_amount?.toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                  <tr className="bg-purple-50">
                    <td className="p-3 border font-bold">Total</td>
                    <td className="p-3 border text-right font-bold text-lg">₹{selectedInvoice.amount?.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-gray-500 text-center mb-4">
                Amount in words: <strong>{numberToWords(selectedInvoice.amount || 0)} Rupees Only</strong>
              </p>

              {selectedInvoice.status === 'paid' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-bold text-green-800 mb-2">Payment Details</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-gray-500">Paid On:</span> {formatDate(selectedInvoice.payment_date)}</p>
                    <p><span className="text-gray-500">Method:</span> {selectedInvoice.payment_method}</p>
                    <p><span className="text-gray-500">Transaction ID:</span> {selectedInvoice.transaction_id}</p>
                    <p><span className="text-gray-500">Paid By:</span> {selectedInvoice.paid_by}</p>
                  </div>
                </div>
              )}

              <div className="text-center mt-6 pt-4 border-t text-gray-500 text-xs">
                <p className="font-medium">Thank you for your business!</p>
                <p className="mt-1">Ncriptech Global Pvt Limited</p>
                <p>2nd Floor, Rainbow Apartment, Kanaipur, Konnagar, West Bengal - 712246</p>
                <p>Phone: 9836807060 | Email: support@cloudcampus.site</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Pay Invoice</h2>
              <p className="text-sm text-gray-500">{selectedInvoice.invoice_number}</p>
            </div>
            
            <form onSubmit={handlePayInvoice} className="p-6 space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-700">Amount to Pay</p>
                <p className="text-3xl font-bold text-purple-900">₹{selectedInvoice.amount?.toLocaleString('en-IN')}</p>
                <p className="text-xs text-purple-600 mt-1">
                  For: {formatDate(selectedInvoice.billing_period_start)} - {formatDate(selectedInvoice.billing_period_end)}
                </p>
              </div>

              {bankDetails && bankDetails.account_number && bankDetails.account_number !== '1234567890123456' && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <p className="font-medium mb-2">Bank Details:</p>
                  <p><span className="text-gray-500">Account:</span> {bankDetails.account_number}</p>
                  <p><span className="text-gray-500">IFSC:</span> {bankDetails.ifsc_code}</p>
                  <p><span className="text-gray-500">UPI:</span> {bankDetails.upi_id}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="bank_transfer">Bank Transfer / NEFT / RTGS</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID / Reference *</label>
                <input
                  type="text"
                  value={paymentForm.transaction_id}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transaction_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter transaction ID"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  placeholder="Additional notes"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={processingPayment}
                  className="flex-1 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm Payment'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPayModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupSubscription;
